function showWishlistToast(message) {
  var toast = document.querySelector('.wishlist-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<div class="wishlist-toast__content">' +
                      '<span class="wishlist-toast__icon">✓</span>' +
                      '<span class="wishlist-toast__message"></span>' +
                      '</div>';
    document.body.appendChild(toast);
  }
  
  var messageEl = toast.querySelector('.wishlist-toast__message');
  messageEl.textContent = message;
  
  if (toast.toastTimer) {
    clearTimeout(toast.toastTimer);
  }
  
  toast.classList.remove('removing');
  toast.classList.add('show');
  
  toast.toastTimer = setTimeout(function() {
    toast.classList.add('removing');
    setTimeout(function() {
      toast.classList.remove('show', 'removing');
    }, 300);
  }, 3000);
}

class WishlistManager {
  constructor() {
    this.apiUrl = window.themeVariables && window.themeVariables.wishlistApiUrl ? window.themeVariables.wishlistApiUrl : 'https://wishlist.zoediy.com/api';
    this.userId = null;
    this.deviceId = null;
    this.wishlistData = { productIds: [], counts: {} };
    this.statsLoaded = Object.create(null);
    this.statsQueued = Object.create(null);
    this.statsQueue = [];
    this.activeStatsRequests = 0;
    this.maxStatsRequests = 3;
    this.statsObserver = null;
    this.syncInFlight = false;
    this.lastServerSync = 0;
    this.initialSyncScheduled = false;
    this.init();
  }

  init() {
    const self = this;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        self.initializeUser();
      });
    } else {
      self.initializeUser();
    }
  }

  initializeUser() {
    this.userId = this.getShopifyCustomerId();
    this.deviceId = this.getOrCreateDeviceId();

    // Attach interaction handling immediately, but defer non-critical network and
    // DOM synchronization until the browser is idle. This keeps the first input
    // path light while preserving wishlist behavior.
    this.attachEventListeners();
    this.scheduleInitialSync();
  }

  scheduleInitialSync() {
    if (this.initialSyncScheduled) return;
    this.initialSyncScheduled = true;

    var self = this;
    var run = function() {
      if (self.userId && self.deviceId) {
        self.mergeDeviceData();
      } else {
        self.loadWishlistFromServer(true);
      }
    };

    run();
  }

  getShopifyCustomerId() {
    if (window.themeVariables && window.themeVariables.customer && window.themeVariables.customer.id) {
      return String(window.themeVariables.customer.id);
    }
    return null;
  }

  getOrCreateDeviceId() {
    var deviceId = this.getCookie('device_id');
    if (!deviceId) {
      deviceId = this.generateUUID();
      this.setCookie('device_id', deviceId, 365);
    }
    return deviceId;
  }

  generateUUID() {
    var d = new Date().getTime();
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      d += performance.now();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  getCookie(name) {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  }

  loadWishlistFromServer(force) {
    var self = this;
    var now = Date.now();

    if (this.syncInFlight) return;

    // A tab visibility change used to trigger a full server sync and complete
    // document rescan every time. Keep the UI fresh without repeating that work.
    if (!force && this.lastServerSync && (now - this.lastServerSync) < 60000) {
      this.refreshWrappers(document);
      this.loadProductStats(document);
      return;
    }

    var url = this.apiUrl + '/wishlist?';
    if (this.userId) {
      url += 'user_id=' + encodeURIComponent(this.userId);
    } else {
      url += 'device_id=' + encodeURIComponent(this.deviceId);
    }

    this.syncInFlight = true;
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.timeout = 10000;

    xhr.onload = function() {
      self.syncInFlight = false;

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText) || {};
          var ids = Array.isArray(data.productIds) ? data.productIds : [];

          data.productIds = ids.map(function(id) {
            return parseInt(id, 10);
          }).filter(function(id) {
            return id && !isNaN(id);
          });
          data.counts = data.counts || {};
          data.handles = data.handles || {};

          self.wishlistData = data;
          self.lastServerSync = Date.now();

          // One linear DOM pass replaces several nested querySelectorAll loops.
          self.refreshWrappers(document);
          self.loadProductStats(document);
          self.updateHeaderWishlistCount();
        } catch (e) {
          console.error('Failed to parse wishlist data:', e);
        }
      }
    };

    xhr.onerror = function() {
      self.syncInFlight = false;
      console.error('Failed to load wishlist from server');
    };

    xhr.ontimeout = function() {
      self.syncInFlight = false;
    };

    xhr.send();
  }

  mergeDeviceData() {
    var self = this;
    var storageKey = 'zoediy:wishlist-merge:' + this.userId + ':' + this.deviceId;
    var lastMerge = 0;

    try {
      lastMerge = parseInt(window.localStorage.getItem(storageKey) || '0', 10);
    } catch (e) {
      lastMerge = 0;
    }

    // Merging on every page created a redundant POST followed by a GET for every
    // signed-in visitor. Merge at most once per day for this user/device pair.
    if (lastMerge && (Date.now() - lastMerge) < 86400000) {
      this.loadWishlistFromServer(true);
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('POST', this.apiUrl + '/user/merge', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 10000;

    var finish = function(success) {
      if (success) {
        try {
          window.localStorage.setItem(storageKey, String(Date.now()));
        } catch (e) {
          // Storage can be unavailable in strict privacy modes; the wishlist still works.
        }
      }
      self.loadWishlistFromServer(true);
    };

    xhr.onload = function() {
      finish(xhr.status >= 200 && xhr.status < 300);
    };
    xhr.onerror = function() {
      finish(false);
    };
    xhr.ontimeout = function() {
      finish(false);
    };

    xhr.send(JSON.stringify({
      user_id: this.userId,
      device_id: this.deviceId
    }));
  }

  toggleWishlist(productId, callback) {
    var self = this;
    var xhr = new XMLHttpRequest();
    xhr.open('POST', this.apiUrl + '/wishlist/toggle', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var response = JSON.parse(xhr.responseText);
          
          if (response.is_active && response.count > 0) {
            if (self.wishlistData.productIds.indexOf(productId) === -1) {
              self.wishlistData.productIds.push(productId);
            }
          } else {
            var idx = self.wishlistData.productIds.indexOf(productId);
            if (idx > -1) {
              self.wishlistData.productIds.splice(idx, 1);
            }
          }
          
          if (response.count > 0) {
            self.wishlistData.counts[productId] = response.count;
          } else {
            delete self.wishlistData.counts[productId];
          }
          
          self.updateTotalCount(productId, response.total_count);
          self.updateButton(productId);
          self.updateCountDisplay(productId);
          
          var translations = window.wishlistTranslations || { added: 'Product added to Wishlist', removed: 'Product removed from wishlist' };
          var message = response.is_active ? translations.added : translations.removed;
          showWishlistToast(message);
          
          if (callback) {
            callback(response);
          }
        } catch (e) {
          console.error('Failed to parse toggle response:', e);
        }
      }
    };
    
    xhr.onerror = function() {
      console.error('Failed to toggle wishlist');
    };
    
    // Get product handle from multiple sources
    var productHandle = null;
    
    // Try 1: Get from wishlist wrapper data attribute
    var wrapper = document.querySelector('.product-item__wishlist-wrapper[data-product-id="' + productId + '"]');
    if (wrapper) {
      productHandle = wrapper.getAttribute('data-product-handle');
    }
    
    // Try 2: Get from product card
    if (!productHandle && wrapper) {
      var productCard = wrapper.closest('product-item') || wrapper.closest('[data-product-handle]');
      if (productCard) {
        productHandle = productCard.getAttribute('data-product-handle');
      }
    }
    
    // Try 3: Get from product link URL
    if (!productHandle && wrapper) {
      var productItem = wrapper.closest('.product-item');
      if (productItem) {
        var productLink = productItem.querySelector('a[href*="/products/"]');
        if (productLink) {
          var href = productLink.getAttribute('href');
          var match = href.match(/\/products\/([^/?#]+)/);
          if (match) {
            productHandle = match[1];
          }
        }
      }
    }
    var payload = { 
      product_id: productId,
      product_handle: productHandle
    };
    if (self.userId) {
      payload.user_id = self.userId;
    } else {
      payload.device_id = self.deviceId;
    }
    xhr.send(JSON.stringify(payload));
  }

  isInWishlist(productId) {
    return this.wishlistData.productIds.indexOf(productId) > -1;
  }

  getProductCount(productId) {
    return this.wishlistData.counts[productId] || 0;
  }

  updateButton(productId) {
    var wrappers = document.querySelectorAll('.product-item__wishlist-wrapper[data-product-id="' + productId + '"]');
    var isInWishlist = this.isInWishlist(productId);
    
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      if (!wrapper.classList) continue;
      
      var totalCount = parseInt(wrapper.getAttribute('data-total-count') || '0', 10);
      
      if (totalCount > 0) {
        wrapper.classList.add('has-count');
        if (isInWishlist) {
          wrapper.classList.add('is-active');
          wrapper.setAttribute('aria-pressed', 'true');
        } else {
          wrapper.classList.remove('is-active');
          wrapper.setAttribute('aria-pressed', 'false');
        }
      } else {
        wrapper.classList.remove('has-count');
        if (isInWishlist) {
          wrapper.classList.add('is-active');
          wrapper.setAttribute('aria-pressed', 'true');
        } else {
          wrapper.classList.remove('is-active');
          wrapper.setAttribute('aria-pressed', 'false');
        }
      }
    }
  }

  updateCountDisplay(productId) {
    var wrappers = document.querySelectorAll('.product-item__wishlist-wrapper[data-product-id="' + productId + '"]');
    
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var countElement = wrapper.querySelector('.product-item__wishlist-count');
      var totalCount = parseInt(wrapper.getAttribute('data-total-count') || '0', 10);
      
      if (countElement) {
        if (totalCount > 0) {
          countElement.textContent = '(' + totalCount + ')';
          if (wrapper.classList) {
            wrapper.classList.add('has-count');
          }
        } else {
          countElement.textContent = '';
          if (wrapper.classList) {
            wrapper.classList.remove('has-count');
          }
        }
      }
    }
  }

  updateTotalCount(productId, totalCount) {
    var wrappers = document.querySelectorAll('.product-item__wishlist-wrapper[data-product-id="' + productId + '"]');
    for (var i = 0; i < wrappers.length; i++) {
      wrappers[i].setAttribute('data-total-count', totalCount || 0);
    }
  }

  updateAllButtons() {
    var productIds = this.wishlistData.productIds;
    for (var i = 0; i < productIds.length; i++) {
      this.updateButton(productIds[i]);
    }
  }

  updateAllCounts() {
    var counts = this.wishlistData.counts;
    var keys = Object.keys(counts);
    for (var i = 0; i < keys.length; i++) {
      var productId = parseInt(keys[i]);
      if (!isNaN(productId)) {
        this.updateCountDisplay(productId);
      }
    }
  }

  initAllProducts() {
    var wrappers = document.querySelectorAll('.product-item__wishlist-wrapper[data-product-id]');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var productId = parseInt(wrapper.getAttribute('data-product-id'));
      if (productId && !isNaN(productId)) {
        this.updateButton(productId);
        this.updateCountDisplay(productId);
      }
    }
  }

  refreshWrappers(root) {
    var scope = root || document;
    var selector = '.product-item__wishlist-wrapper[data-product-id]';
    var wrappers = [];

    if (scope.matches && scope.matches(selector)) {
      wrappers.push(scope);
    }
    if (scope.querySelectorAll) {
      wrappers = wrappers.concat(Array.prototype.slice.call(scope.querySelectorAll(selector)));
    }

    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var productId = parseInt(wrapper.getAttribute('data-product-id'), 10);
      if (!productId || isNaN(productId)) continue;

      var isActive = this.isInWishlist(productId);
      var totalCount = parseInt(wrapper.getAttribute('data-total-count') || '0', 10);
      var countElement = wrapper.querySelector('.product-item__wishlist-count');

      wrapper.classList.toggle('is-active', isActive);
      wrapper.classList.toggle('has-count', totalCount > 0);
      wrapper.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      if (countElement) {
        countElement.textContent = totalCount > 0 ? '(' + totalCount + ')' : '';
      }
    }
  }

  loadProductStats(root) {
    var scope = root || document;
    var selector = '.product-item__wishlist-wrapper[data-product-id]';
    var wrappers = [];
    var seen = Object.create(null);
    var self = this;

    if (scope.matches && scope.matches(selector)) {
      wrappers.push(scope);
    }
    if (scope.querySelectorAll) {
      wrappers = wrappers.concat(Array.prototype.slice.call(scope.querySelectorAll(selector)));
    }

    if (wrappers.length === 0) return;

    if ('IntersectionObserver' in window && !this.statsObserver) {
      this.statsObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;

          self.statsObserver.unobserve(entry.target);
          var productId = parseInt(entry.target.getAttribute('data-product-id'), 10);
          if (!productId || isNaN(productId)) return;

          if (self.statsQueued[productId] === 'observing') {
            delete self.statsQueued[productId];
            self.enqueueProductStat(productId);
          }
        });
      }, { rootMargin: '500px 0px' });
    }

    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var productId = parseInt(wrapper.getAttribute('data-product-id'), 10);

      if (!productId || isNaN(productId) || seen[productId] || this.statsLoaded[productId] || this.statsQueued[productId]) {
        continue;
      }

      seen[productId] = true;

      if (this.statsObserver) {
        this.statsQueued[productId] = 'observing';
        this.statsObserver.observe(wrapper);
      } else {
        this.enqueueProductStat(productId);
      }
    }
  }

  enqueueProductStat(productId) {
    if (!productId || this.statsLoaded[productId] || this.statsQueued[productId]) return;

    this.statsQueued[productId] = 'queued';
    this.statsQueue.push(productId);
    this.drainProductStatsQueue();
  }

  drainProductStatsQueue() {
    var self = this;

    while (this.activeStatsRequests < this.maxStatsRequests && this.statsQueue.length > 0) {
      var productId = this.statsQueue.shift();

      if (this.statsLoaded[productId]) {
        delete this.statsQueued[productId];
        continue;
      }

      this.activeStatsRequests++;
      this.requestProductStat(productId, function() {
        self.activeStatsRequests--;
        self.drainProductStatsQueue();
      });
    }
  }

  requestProductStat(productId, done) {
    var self = this;
    var xhr = new XMLHttpRequest();
    var completed = false;

    var finish = function(success) {
      if (completed) return;
      completed = true;
      delete self.statsQueued[productId];
      if (success) self.statsLoaded[productId] = true;
      done();
    };

    xhr.open('GET', this.apiUrl + '/product/' + productId + '/stats', true);
    xhr.timeout = 8000;

    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var stats = JSON.parse(xhr.responseText);
          self.updateProductTotalCount(productId, stats.total_count || 0);
          finish(true);
          return;
        } catch (e) {
          console.error('Failed to parse product stats:', e);
        }
      }
      finish(false);
    };

    xhr.onerror = function() {
      finish(false);
    };
    xhr.ontimeout = function() {
      finish(false);
    };

    xhr.send();
  }

  updateProductTotalCount(productId, totalCount) {
    var wrappers = document.querySelectorAll('.product-item__wishlist-wrapper[data-product-id="' + productId + '"]');
    
    for (var i = 0; i < wrappers.length; i++) {
      wrappers[i].setAttribute('data-total-count', totalCount || 0);
    }
    
    this.updateCountDisplay(productId);
  }

  updateHeaderWishlistCount() {
    var headerCount = document.getElementById('header-wishlist-count');
    if (!headerCount) return;
    
    var count = this.wishlistData.productIds.length;
    
    if (count > 0) {
      headerCount.textContent = count;
      headerCount.style.display = '';
    } else {
      headerCount.style.display = 'none';
    }
  }

  attachEventListeners() {
    var self = this;

    if (this.eventListenerAttached) return;
    this.eventListenerAttached = true;

    document.addEventListener('visibilitychange', function() {
      if (!document.hidden) {
        self.loadWishlistFromServer(false);
      }
    });

    window.addEventListener('pageshow', function(event) {
      if (event.persisted) {
        self.loadWishlistFromServer(false);
      }
    });

    // Product recommendations are inserted after their Shopify section request.
    // Update only the newly inserted subtree instead of rescanning repeatedly.
    document.addEventListener('theme:product-cards-added', function(event) {
      var root = event.detail && event.detail.container ? event.detail.container : document;
      self.refreshWrappers(root);
      self.loadProductStats(root);
    });

    document.addEventListener('click', function(e) {
      var target = e.target;
      var wrapper = null;

      while (target && target !== document) {
        if (target.classList && target.classList.contains('product-item__wishlist-wrapper')) {
          wrapper = target;
          break;
        }
        target = target.parentElement;
      }

      if (!wrapper) return;

      e.preventDefault();
      e.stopPropagation();

      var productId = parseInt(wrapper.getAttribute('data-product-id'), 10);
      if (!productId || isNaN(productId)) return;

      // ZoeDIY INP v10: acknowledge the tap immediately, before network work.
      if (wrapper.classList) {
        wrapper.classList.add('wishlist-animate');
        setTimeout(function() {
          wrapper.classList.remove('wishlist-animate');
        }, 300);
      }

      self.toggleWishlist(productId, function(response) {
        self.updateButton(productId);
        self.updateCountDisplay(productId);
        self.updateHeaderWishlistCount();

        if (self.isWishlistPage && !response.is_active) {
          self.removeFromWishlistPage(productId);
        }
      });
    });

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;

      var target = e.target;
      if (!target || !target.classList || !target.classList.contains('product-item__wishlist-wrapper')) return;

      e.preventDefault();
      target.click();
    });
  }

  // Wishlist Page Methods
  initWishlistPage() {
    if (!document.getElementById('wishlist-grid')) return;
    
    this.currentPage = 1;
    this.itemsPerPage = 20;
    this.loadWishlistPage();
    this.attachWishlistPageListeners();
  }

  loadWishlistPage() {
    var self = this;
    var loadingEl = document.getElementById('wishlist-loading');
    var emptyEl = document.getElementById('wishlist-empty');
    var gridEl = document.getElementById('wishlist-grid');
    var container = document.getElementById('wishlist-products-container');
    var countEl = document.getElementById('wishlist-item-count');
    
    
    if (loadingEl) loadingEl.style.display = 'block';
    if (emptyEl) emptyEl.style.display = 'none';
    if (gridEl) gridEl.style.display = 'none';
    
    var url = this.apiUrl + '/wishlist?';
    if (this.userId) {
      url += 'user_id=' + encodeURIComponent(this.userId);
    } else {
      url += 'device_id=' + encodeURIComponent(this.deviceId);
    }
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onload = function() {
      if (loadingEl) loadingEl.style.display = 'none';
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText);
          self.wishlistData = data;
          if (data.productIds.length === 0) {
            if (emptyEl) emptyEl.style.display = 'block';
            if (countEl) countEl.textContent = '0';
            return;
          }
          
          if (countEl) countEl.textContent = data.productIds.length;
          if (gridEl) {
            gridEl.style.display = 'block';
          }
          self.renderWishlistProducts(data.productIds.slice(0, self.itemsPerPage));
          self.updateLoadMoreButton(data.productIds.length);
        } catch (e) {
          console.error('Failed to parse wishlist data:', e);
        }
      } else {
        console.error('API error:', xhr.status, xhr.responseText);
      }
    };
    xhr.onerror = function() {
      if (loadingEl) loadingEl.style.display = 'none';
      console.error('Failed to load wishlist - network error');
    };
    xhr.send();
  }

  renderWishlistProducts(productIds) {
    var container = document.getElementById('wishlist-products-container');
    if (!container) return;
    
    if (this.currentPage === 1) {
      container.innerHTML = '';
    }
    
    var self = this;
    productIds.forEach(function(productId) {
      var productHandle = self.wishlistData.handles && self.wishlistData.handles[productId];
      self.fetchAndRenderProduct(productId, productHandle, container);
    });
  }

  fetchAndRenderProduct(productId, productHandle, container) {
    var self = this;
    // First, get product stats
    var xhr = new XMLHttpRequest();
    xhr.open('GET', this.apiUrl + '/product/' + productId + '/stats', true);
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var stats = JSON.parse(xhr.responseText);
          // If we have a valid handle, use it directly (fastest method)
          if (productHandle && productHandle !== 'null' && productHandle !== null) {
            var productXhr = new XMLHttpRequest();
            productXhr.open('GET', '/products/' + productHandle + '.js', true);
            productXhr.onload = function() {
              if (productXhr.status >= 200 && productXhr.status < 300) {
                try {
                  var product = JSON.parse(productXhr.responseText);
                  self.renderProductCard(product, container, stats);
                } catch (e) {
                  console.error('❌ Failed to parse product by handle:', e);
                  self.fetchProductByIdFallback(productId, container, stats);
                }
              } else {
                console.error('❌ Failed to fetch product by handle (HTTP ' + productXhr.status + ')');
                self.fetchProductByIdFallback(productId, container, stats);
              }
            };
            productXhr.onerror = function() {
              console.error('❌ Network error fetching product by handle');
              self.fetchProductByIdFallback(productId, container, stats);
            };
            productXhr.send();
          } else {
            // No handle available, use fallback method
            self.fetchProductByIdFallback(productId, container, stats);
          }
        } catch (e) {
          console.error('Failed to parse product stats:', productId, e);
        }
      } else {
        console.error('Failed to fetch product stats:', productId, xhr.status, xhr.responseText);
      }
    };
    xhr.onerror = function() {
      console.error('Network error fetching product stats:', productId);
    };
    xhr.send();
  }

  fetchProductByIdFallback(productId, container, stats) {
    var self = this;
    // Try to fetch from /products.json with pagination
    var page = 1;
    var maxPages = 10; // Limit to prevent infinite loop
    
    function tryFetchPage(pageNum) {
      if (pageNum > maxPages) {
        console.error('❌ Product', productId, 'not found after checking', maxPages, 'pages');
        return;
      }
      
      var xhr = new XMLHttpRequest();
      xhr.open('GET', '/products.json?limit=250&page=' + pageNum, true);
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var response = JSON.parse(xhr.responseText);
            var products = response.products || [];
            // Find product by ID (compare as numbers)
            for (var i = 0; i < products.length; i++) {
              var currentId = parseInt(products[i].id);
              var targetId = parseInt(productId);
              
              if (currentId === targetId) {
                self.renderProductCard(products[i], container, stats);
                return;
              }
            }
            
            // If not found and there are more products, try next page
            if (products.length === 250) {
              tryFetchPage(pageNum + 1);
            } else {
              console.error('❌ Product', productId, 'not found in any page (checked', pageNum, 'pages total)');
            }
          } catch (e) {
            console.error('❌ Error parsing page', pageNum, ':', e);
          }
        } else {
          console.error('❌ HTTP error fetching page', pageNum, ':', xhr.status);
        }
      };
      xhr.onerror = function() {
        console.error('❌ Network error fetching page', pageNum);
      };
      xhr.send();
    }
    
    tryFetchPage(1);
  }

  renderProductCard(product, container, stats) {
    // ZoeDIY: retired products remain reachable by direct URL, but do not render in wishlist/discovery surfaces.
    var productTags = product && product.tags;
    var isRetiredKeepPage = Array.isArray(productTags)
      ? productTags.indexOf('retired-keep-page') !== -1
      : (typeof productTags === 'string' && productTags.split(',').some(function(tag) {
          return tag.trim() === 'retired-keep-page';
        }));

    if (isRetiredKeepPage) {
      return;
    }
    
    var productUrl = '/products/' + product.handle;
    
    // Get price from first variant
    // Shopify JSON APIs return price in CENTS as integer (e.g., 280 = $2.80)
    var variant = product.variants && product.variants[0];
    var priceInCents = variant ? parseInt(variant.price, 10) : 0;
    var compareAtPriceInCents = variant && variant.compare_at_price ? parseInt(variant.compare_at_price, 10) : 0;
    
    var price = this.formatMoney(priceInCents);
    var comparePrice = compareAtPriceInCents > 0 ? this.formatMoney(compareAtPriceInCents) : '';
    
    var totalCount = stats && stats.total_count ? stats.total_count : 0;
    var isActive = this.wishlistData.productIds.indexOf(product.id) !== -1;
    
    // Build wishlist wrapper classes
    var wishlistClasses = 'product-item__wishlist-wrapper';
    if (isActive) {
      wishlistClasses += ' is-active';
    }
    if (totalCount > 0) {
      wishlistClasses += ' has-count';
    }
    
    // Build image HTML - use first image from images array
    var imageHtml = '';
    var aspectRatioStyle = '';
    
    if (product.images && product.images.length > 0) {
      // Images can be either a string array or object array
      var imgSrc = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
      
      // Ensure absolute URL (add https: if protocol-relative)
      if (imgSrc && imgSrc.indexOf('//') === 0) {
        imgSrc = 'https:' + imgSrc;
      }
      // Get image dimensions for aspect ratio (use featured_media if available, otherwise default to 1:1)
      var imageWidth = 500;
      var imageHeight = 500;
      if (product.featured_media && product.featured_media.preview_image) {
        imageWidth = product.featured_media.preview_image.width || 500;
        imageHeight = product.featured_media.preview_image.height || 500;
      } else if (product.image && product.image.width) {
        imageWidth = product.image.width;
        imageHeight = product.image.height || imageWidth;
      }
      
      var aspectRatio = imageWidth / imageHeight;
      var paddingBottom = (100.0 / aspectRatio).toFixed(2);
      aspectRatioStyle = 'padding-bottom: ' + paddingBottom + '%; --aspect-ratio: ' + aspectRatio.toFixed(4);
      
      imageHtml = '<img src="' + imgSrc + '" alt="' + (product.title || '').replace(/"/g, '&quot;') + '" loading="lazy" class="product-item__primary-image">';
    } else {
      console.warn('⚠️ No images found for product:', product.id);
      // Default aspect ratio for empty state
      aspectRatioStyle = 'padding-bottom: 100%; --aspect-ratio: 1';
    }
    
    // Build price HTML
    var priceHtml = '';
    if (compareAtPriceInCents > priceInCents) {
      priceHtml = '<span class="price price--highlight">' + price + '</span>' +
                  '<span class="price price--compare">' + comparePrice + '</span>';
    } else {
      priceHtml = '<span class="price">' + price + '</span>';
    }
    
    // Build sold out label if needed
    var soldOutLabel = product.available ? '' : '<span class="label label--subdued">Sold out</span>';
    
    // Create product item HTML (matching product-item.liquid structure)
    var productItemHtml = 
      '<product-item class="product-item' + (product.available ? '' : ' product-item--sold-out') + '" data-product-handle="' + (product.handle || '') + '">' +
        '<div class="product-item__image-wrapper">' +
          (soldOutLabel ? '<div class="product-item__label-list label-list">' + soldOutLabel + '</div>' : '') +
          '<div class="' + wishlistClasses + '" data-product-id="' + product.id + '" data-total-count="' + totalCount + '" data-product-handle="' + (product.handle || '') + '">' +
            '<svg class="wishlist-icon wishlist-icon--outline' + (isActive ? '' : ' active') + '" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--wishlist-outline-color)" stroke-width="1.5">' +
              '<path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>' +
            '</svg>' +
            '<svg class="wishlist-icon wishlist-icon--filled' + (isActive ? ' active' : '') + '" width="22" height="22" viewBox="0 0 24 24" fill="var(--wishlist-filled-color)">' +
              '<path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>' +
            '</svg>' +
            '<span class="product-item__wishlist-count">' + (totalCount > 0 ? '(' + totalCount + ')' : '') + '</span>' +
          '</div>' +
          '<a href="' + productUrl + '" class="product-item__aspect-ratio aspect-ratio" style="' + aspectRatioStyle + '">' +
            imageHtml +
          '</a>' +
        '</div>' +
        '<div class="product-item__info">' +
          '<div class="product-item-meta">' +
            '<a href="' + productUrl + '" class="product-item-meta__title">' + (product.title || 'Product') + '</a>' +
            '<div class="price-list">' + priceHtml + '</div>' +
          '</div>' +
        '</div>' +
      '</product-item>';
    
    container.insertAdjacentHTML('beforeend', productItemHtml);
  }

  formatMoney(cents) {
    // Use Shopify's money format from theme settings for multi-currency support
    var format = window.themeVariables && window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : (window.themeVariables && window.themeVariables.settings.moneyFormat);
    
    // Fallback to simple USD format if theme variables not available
    if (!format) {
      format = '${{amount}}';
    }
    
    // Convert cents to dollars with 2 decimal places
    var amount = (cents / 100).toFixed(2);
    
    // Replace placeholders in format string
    // {{amount}} = amount with decimals (e.g., "2.80")
    // {{amount_no_decimals}} = amount without decimals (e.g., "3")
    var formattedPrice = format.replace('{{amount}}', amount);
    formattedPrice = formattedPrice.replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
    
    return formattedPrice;
  }

  updateLoadMoreButton(totalItems) {
    var paginationEl = document.getElementById('wishlist-pagination');
    var loadMoreBtn = document.getElementById('load-more-btn');
    
    if (!paginationEl || !loadMoreBtn) return;
    
    var displayedItems = this.currentPage * this.itemsPerPage;
    
    if (displayedItems < totalItems) {
      paginationEl.style.display = 'block';
    } else {
      paginationEl.style.display = 'none';
    }
  }

  attachWishlistPageListeners() {
    var self = this;
    
    // Load More button
    var loadMoreBtn = document.getElementById('load-more-btn');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function() {
        self.currentPage++;
        var start = (self.currentPage - 1) * self.itemsPerPage;
        var end = start + self.itemsPerPage;
        var productIds = self.wishlistData.productIds.slice(start, end);
        self.renderWishlistProducts(productIds);
        self.updateLoadMoreButton(self.wishlistData.productIds.length);
      });
    }
    
    // Note: Wishlist toggle is handled by the global event listener in attachEventListeners()
    // When a product is removed from wishlist on this page, we need to handle it specially
    this.isWishlistPage = true;
  }

  removeFromWishlistPage(productId) {
    var self = this;
    
    // Update button state on all pages (not just wishlist page)
    // This ensures when user goes back, the button shows correct state
    self.updateButton(productId);
    self.updateCountDisplay(productId);
    
    // Find the product-item element on wishlist page
    var productItem = document.querySelector('.product-item__wishlist-wrapper[data-product-id="' + productId + '"]');
    if (productItem) {
      var item = productItem.closest('product-item');
      if (item) {
        item.style.opacity = '0';
        item.style.transition = 'opacity 0.3s';
        setTimeout(function() {
          item.remove();
          
          // Update count
          var countEl = document.getElementById('wishlist-item-count');
          if (countEl) {
            var currentCount = parseInt(countEl.textContent) - 1;
            countEl.textContent = currentCount;
            
            // Show empty state if no items
            if (currentCount === 0) {
              document.getElementById('wishlist-grid').style.display = 'none';
              document.getElementById('wishlist-empty').style.display = 'block';
            }
          }
        }, 300);
      }
    }
  }
}

// Singleton pattern to prevent duplicate instances
if (!window.wishlistManager) {
  window.wishlistManager = new WishlistManager();
}
